// frontend/src/pages/SettingsPage.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  User, Lock, Camera, Save, ArrowLeft,
  Eye, EyeOff, CheckCircle, AlertCircle, Loader
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { authAPI } from '../services/api';
import api from '../services/api';

// ─────────────────────────────────────────────
// ALERT COMPONENT
// ─────────────────────────────────────────────
const Alert: React.FC<{ type: 'success' | 'error'; message: string }> = ({ type, message }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`flex items-center gap-2 p-3 rounded-xl text-sm mb-4 ${
      type === 'success'
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-red-50 text-red-700 border border-red-200'
    }`}
  >
    {type === 'success'
      ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
      : <AlertCircle className="w-4 h-4 flex-shrink-0" />
    }
    {message}
  </motion.div>
);

// ─────────────────────────────────────────────
// MAIN SETTINGS PAGE
// ─────────────────────────────────────────────
const SettingsPage: React.FC = () => {
  const { state, updateProfile, openLoginModal } = useAppContext();
  const navigate = useNavigate();
  const user = state.currentUser;

  // ── Profile form state ──
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
  });
  const [profileAlert, setProfileAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // ── Password form state ──
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordAlert, setPasswordAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // ── Avatar state ──
  const [avatarAlert, setAvatarAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  // ── Not logged in ──
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Please sign in to access settings</p>
          <button onClick={openLoginModal} className="btn-primary">Sign In</button>
        </div>
      </div>
    );
  }

  // ── Save profile ──
  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      setProfileAlert({ type: 'error', message: 'Name cannot be empty' });
      return;
    }
    if (!profileForm.username.trim() || profileForm.username.length < 3) {
      setProfileAlert({ type: 'error', message: 'Username must be at least 3 characters' });
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(profileForm.username)) {
      setProfileAlert({ type: 'error', message: 'Username can only contain letters, numbers and underscores' });
      return;
    }

    try {
      setIsSavingProfile(true);
      setProfileAlert(null);
      await updateProfile({
        name: profileForm.name,
        username: profileForm.username,
        bio: profileForm.bio,
      });
      setProfileAlert({ type: 'success', message: 'Profile updated successfully!' });
    } catch (err: any) {
      setProfileAlert({
        type: 'error',
        message: err.response?.data?.message || 'Failed to update profile',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  // ── Change password ──
  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordAlert({ type: 'error', message: 'Please fill in all password fields' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordAlert({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordAlert({ type: 'error', message: 'New passwords do not match' });
      return;
    }
    if (passwordForm.newPassword === passwordForm.currentPassword) {
      setPasswordAlert({ type: 'error', message: 'New password must be different from current password' });
      return;
    }

    try {
      setIsSavingPassword(true);
      setPasswordAlert(null);
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordAlert({ type: 'success', message: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordAlert({
        type: 'error',
        message: err.response?.data?.message || 'Failed to change password',
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAvatarAlert({ type: 'error', message: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAvatarAlert({ type: 'error', message: 'Image must be under 5MB' });
      return;
    }

    try {
      setIsSavingAvatar(true);
      setAvatarAlert(null);

      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/auth/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await updateProfile({ avatar: response.data.data.avatarUrl });
      setAvatarAlert({ type: 'success', message: 'Profile picture updated!' });
    } catch (err: any) {
      setAvatarAlert({ type: 'error', message: 'Failed to upload image' });
    } finally {
      setIsSavingAvatar(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-gray-500 hover:text-romcom-red transition-colors mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </button>
          <h1 className="text-4xl font-playfair font-bold text-gray-800">Settings</h1>
          <p className="text-gray-500 mt-1">Manage your account and preferences</p>
        </motion.div>

        {/* ── PROFILE PICTURE ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#faf8f6] rounded-2xl p-6 shadow-sm border border-romcom-pink/20 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-romcom-red" />
            Profile Picture
          </h2>

          {avatarAlert && <Alert type={avatarAlert.type} message={avatarAlert.message} />}

          <div className="flex items-center gap-6">
            {/* Current avatar */}
            <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 shadow-md">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-2xl font-bold">
                  {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="flex-1">
              <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-romcom-red text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors w-fit">
                    <Camera className="w-4 h-4" />
                    {isSavingAvatar ? 'Uploading...' : 'Upload Photo'}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={isSavingAvatar}
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">JPG, PNG or WebP. Max 5MB.</p>
                     </div>
          </div>
        </motion.div>

        {/* ── PROFILE INFO ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#faf8f6] rounded-2xl p-6 shadow-sm border border-romcom-pink/20 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-romcom-red" />
            Profile Information
          </h2>

          {profileAlert && <Alert type={profileAlert.type} message={profileAlert.message} />}

          <div className="space-y-4">
            {/* Display name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={e => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none"
                placeholder="Your display name"
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                <input
                  type="text"
                  value={profileForm.username}
                  onChange={e => setProfileForm(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none"
                  placeholder="your_username"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Letters, numbers and underscores only</p>
            </div>

            {/* Email (read only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-4 py-3 border border-gray-100 rounded-xl bg-gray-50 text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={profileForm.bio}
                onChange={e => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none resize-none"
                placeholder="Tell us about your romcom journey..."
                maxLength={200}
              />
              <p className="text-xs text-gray-400 mt-1">{profileForm.bio.length}/200 characters</p>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={isSavingProfile}
              className="w-full flex items-center justify-center gap-2 bg-romcom-red text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {isSavingProfile ? (
                <><Loader className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4" /> Save Profile</>
              )}
            </button>
          </div>
        </motion.div>

        {/* ── CHANGE PASSWORD ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#faf8f6] rounded-2xl p-6 shadow-sm border border-romcom-pink/20 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-romcom-red" />
            Change Password
          </h2>

          {passwordAlert && <Alert type={passwordAlert.type} message={passwordAlert.message} />}

          <div className="space-y-4">
            {/* Current password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={e => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {passwordForm.newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map(level => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordForm.newPassword.length >= level * 3
                            ? level <= 1 ? 'bg-red-400'
                              : level <= 2 ? 'bg-yellow-400'
                              : level <= 3 ? 'bg-blue-400'
                              : 'bg-green-400'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {passwordForm.newPassword.length < 4 ? 'Too short' :
                     passwordForm.newPassword.length < 7 ? 'Weak' :
                     passwordForm.newPassword.length < 10 ? 'Good' : 'Strong'} password
                  </p>
                </div>
              )}
            </div>

            {/* Confirm new password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`w-full px-4 py-3 pr-12 border rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none ${
                    passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                      ? 'border-red-300 bg-red-50'
                      : passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200'
                  }`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              onClick={handleChangePassword}
              disabled={isSavingPassword}
              className="w-full flex items-center justify-center gap-2 bg-romcom-red text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {isSavingPassword ? (
                <><Loader className="w-4 h-4 animate-spin" /> Changing Password...</>
              ) : (
                <><Lock className="w-4 h-4" /> Change Password</>
              )}
            </button>
          </div>
        </motion.div>

        {/* ── DANGER ZONE ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#faf8f6] rounded-2xl p-6 shadow-sm border border-red-100"
        >
          <h2 className="text-xl font-bold text-red-600 mb-2">Danger Zone</h2>
          <p className="text-gray-500 text-sm mb-4">
            Once you delete your account, there is no going back. All your reviews, favorites and watchlist will be permanently deleted.
          </p>
          <button
            onClick={() => {
              if (window.confirm('Are you absolutely sure? This cannot be undone.')) {
                alert('Account deletion coming soon. Please contact support.');
              }
            }}
            className="px-6 py-2.5 border-2 border-red-300 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
          >
            Delete Account
          </button>
        </motion.div>

      </div>
    </div>
  );
  };

export default SettingsPage;